import { createContext, useContext, useState, type ReactNode } from 'react';
import { createTRPCClient, httpBatchLink, httpSubscriptionLink, splitLink } from '@trpc/client';
import SuperJSON from 'superjson';
import type { AdminTestApiRouter } from '@duke-hq/app-admin-test-server/api/router';

// Local development only: the admin test-server is reachable at `/test/api` via
// the Vite dev-server proxy (vite.config.ts), never in a deployed environment.
//
// This is a plain tRPC client (not a second createTRPCReact provider): classic
// createTRPCReact instances share one React context, so nesting a second
// provider would override the trpcAdmin client for every trpc hook underneath
// it -- which is what sent the provider/course dropdown queries to the
// test-server. Driving the seed calls through this client keeps trpcAdmin the
// only trpc React context.
function createAdminTestClient() {
  return createTRPCClient<AdminTestApiRouter>({
    links: [
      splitLink({
        condition: (op) => op.type === 'subscription',
        true: httpSubscriptionLink({ url: '/test/api', transformer: SuperJSON }),
        false: httpBatchLink({ url: '/test/api', transformer: SuperJSON }),
      }),
    ],
  });
}

type AdminTestClient = ReturnType<typeof createAdminTestClient>;

const AdminTestClientContext = createContext<AdminTestClient | undefined>(undefined);

export function AdminTestClientProvider({ children }: { children: ReactNode }) {
  return import.meta.env.MODE === 'development' ? (
    <AdminTestClientProviderInner>{children}</AdminTestClientProviderInner>
  ) : (
    children
  );
}

export function AdminTestClientProviderInner({ children }: { children: ReactNode }) {
  const [client] = useState(() => createAdminTestClient());
  return (
    <AdminTestClientContext.Provider value={client}>{children}</AdminTestClientContext.Provider>
  );
}

export function useAdminTestClient(): AdminTestClient {
  const client = useContext(AdminTestClientContext);
  if (!client) {
    throw new Error('useAdminTestClient must be used within an AdminTestClientProvider');
  }
  return client;
}
